/**
 * Buyer-facing Safety Management presentation.
 * Authority is finding_id. Does not change backend semantics.
 *
 * Attention priority (highest first):
 *  1. blocking / error
 *  2. current Scan requires formal verification
 *  3. owner or action required
 *  4. Control required / follow-up
 *  5. managed new activity
 *  6. monitoring / no inbox action
 */

import type { ControlStage } from './workspaceResolvedState';

export const BUYER_CASE_STATES = [
  'Monitoring',
  'Needs review',
  'Control recorded',
  'Follow-up pending',
  'Closed',
] as const;

export type BuyerCaseState = (typeof BUYER_CASE_STATES)[number];

export const ATTENTION_PRIORITY_ORDER = [
  'blocking_error',
  'current_scan_verification',
  'owner_or_action_required',
  'control_or_followup',
  'managed_new_activity',
  'monitoring_no_action',
] as const;

export type AttentionBuyerPriority = (typeof ATTENTION_PRIORITY_ORDER)[number];

export const REVIEW_SAFETY_SCAN_LABEL = 'Review Safety Scan';
export const OPEN_SAFETY_CASE_LABEL = 'Open Safety Case';
export const INVESTIGATE_LABEL = 'Investigate';
export const RECORD_CONTROL_LABEL = 'Record Control';
export const MONITORING_LABEL = 'Monitoring';
export const CONTINUE_MONITORING_LABEL = 'Continue monitoring';

export function mapBuyerCaseState(input: {
  controlStage?: ControlStage | string | null;
  hasNewActivity?: boolean;
  scanCount?: number | null;
}): BuyerCaseState {
  const stage = String(input.controlStage || '').trim();
  if (stage === 'Closed') return 'Closed';
  if (input.scanCount === 0 && !input.hasNewActivity) return 'Monitoring';
  if (stage === 'Follow-up measurement scheduled') return 'Follow-up pending';
  if (
    stage === 'Control under review'
    || stage === 'Action assigned'
    || stage === 'Implementation in progress'
    || stage === 'Implementation verification required'
  ) {
    return 'Control recorded';
  }
  if (input.hasNewActivity || stage === 'Reopened' || stage === 'Effectiveness review required') {
    return 'Needs review';
  }
  return 'Monitoring';
}

type AttentionPriorityInput = {
  metricKey?: string | null;
  actionKind?: string | null;
  isUnowned?: boolean;
  itemType?: string | null;
  safetyCaseId?: string | null;
};

export function attentionBuyerPriority(item: AttentionPriorityInput): AttentionBuyerPriority {
  if (item.metricKey === 'coverage_run' || item.actionKind === 'resolve_coverage') return 'blocking_error';
  if (item.metricKey === 'awaiting_signoff' || item.actionKind === 'sign_scan') return 'current_scan_verification';
  if (item.actionKind === 'assign_owner' || item.isUnowned) return 'owner_or_action_required';
  if (item.metricKey === 'new_activity') return 'managed_new_activity';
  if (item.metricKey === 'needs_review' || item.actionKind === 'review_pattern') return 'owner_or_action_required';
  if (item.metricKey === 'active_cases') return 'monitoring_no_action';
  return 'control_or_followup';
}

export function attentionBuyerPriorityRank(item: AttentionPriorityInput): number {
  return ATTENTION_PRIORITY_ORDER.indexOf(attentionBuyerPriority(item));
}

export function dedupeManagedAttentionItems<T extends AttentionPriorityInput & { safetyCaseId?: string | null }>(items: T[]): T[] {
  const unmanaged: T[] = [];
  const byFinding = new Map<string, T>();
  for (const item of items) {
    const findingId = String(item.safetyCaseId || '').trim();
    if (!findingId || item.itemType === 'risk_pattern_review') {
      unmanaged.push(item);
      continue;
    }
    const existing = byFinding.get(findingId);
    if (!existing) {
      byFinding.set(findingId, item);
      continue;
    }
    if (attentionBuyerPriorityRank(item) < attentionBuyerPriorityRank(existing)) {
      byFinding.set(findingId, item);
    }
  }
  return [...unmanaged, ...Array.from(byFinding.values())];
}

export function presentDominantNextAction(input: {
  canManage: boolean;
  scanCount: number;
  currentScanNeedsVerification: boolean;
  hasControl: boolean;
  hasNewActivity?: boolean;
}): { label: string; isAction: boolean; kind: 'review_scan' | 'record_control' | 'monitoring' } {
  if (!input.canManage || input.scanCount <= 0) {
    return { label: MONITORING_LABEL, isAction: false, kind: 'monitoring' };
  }
  if (input.currentScanNeedsVerification) {
    return { label: REVIEW_SAFETY_SCAN_LABEL, isAction: true, kind: 'review_scan' };
  }
  if (!input.hasControl) {
    return { label: RECORD_CONTROL_LABEL, isAction: true, kind: 'record_control' };
  }
  return { label: CONTINUE_MONITORING_LABEL, isAction: false, kind: 'monitoring' };
}
